import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  IonPage,
  IonToolbar,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  useIonViewDidEnter,
  IonText,
  IonList,
  IonTitle,
  useIonViewWillLeave,
  IonSpinner,
} from '@ionic/react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import './DocumentScanner.page.css'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { getUserSelector } from '../../user/selectors'
import { AuthenticationService } from '../../login/services/authentication.service'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import DocumentItem from '../documentItem/DocumentItem.component'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { v4 as uuidv4 } from 'uuid'
import { IScannedDocument } from '../store/documentScanner.types'
import { getDocumentScannerSelector } from '../selectors/documentScanner.selector'
import { importDocuments, setDisableScanButton, setScannedDocuments } from '../store/documentScanner.actions'
import { IOption } from '../../../common/common.types'
import { isIOSDevice } from '../../../utilities/isiOS.util'
import { WeScan } from 'capacitor-plugin-wescan'
import { DocumentScanner as DocumentScannerPlugin, ResponseType } from 'capacitor-android-document-scanner'
import { Camera } from '@capacitor/camera'
import Logger from '../../../services/logger.service'
import { IServiceConfig } from '../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'

const DocumentScanner: React.FC = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const user = useSelector(getUserSelector)
  const { PR_REQ_CHART_ATTACH_CATEGORY }: IServiceConfig = useSelector(getServiceConfigSelector)
  const { scannedDocuments } = useSelector(getDocumentScannerSelector)

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [scannerError, setScannerError] = useState<string | null>(null)
  const isActiveRef = useRef<boolean>(false)

  const isIOS = isIOSDevice()

  const allowSecureRoutes = AuthenticationService.allowSecureRoutes(user)
  useEffect(() => {
    if (!allowSecureRoutes) {
      Logger.warn('User is not authenticated. Redirecting to index route.')
      history.push(AppRouterConstants.INDEX_ROUTE)
    }
  }, [allowSecureRoutes])

  useIonViewDidEnter(() => {
    isActiveRef.current = true
    setIsLoading(true)
    onScan()
  })

  useIonViewWillLeave(() => {
    isActiveRef.current = false
    setScannerError(null)
    dispatch(setScannedDocuments([]))
  })

  const onClose = () => {
    history.push(TabConstants.ATTACHMENT_TAB)
  }

  const onCancel = () => {
    Logger.info('DocumentScanner: User canceled out of the scanned document editing screen')
    onClose()
  }

  const onBackClick = () => {
    if (scannedDocuments?.length === 0) {
      onClose()
      return
    }

    const alertState = {
      header: 'Warning',
      message: 'You have not saved this attachment data, are you sure you want to change pages and clear this data?',
      buttons: [{ text: 'Cancel' }, { text: 'Ok', role: 'destructive', handler: onCancel }],
    }
    dispatch(showAlert(alertState))
  }

  const checkCameraPermissions = async (): Promise<boolean> => {
    const permissionStatus = await Camera.checkPermissions()

    if (permissionStatus.camera === 'prompt' || permissionStatus.camera === 'prompt-with-rationale') {
      const newPermissionStatus = await Camera.requestPermissions({ permissions: ['camera'] })

      if (newPermissionStatus.camera !== 'granted') {
        setScannerError(t('documentScanner.PermissionRequired'))
        return false
      }
    }

    if (permissionStatus.camera === 'denied') {
      setScannerError(t('documentScanner.PermissionRequired'))
      return false
    }

    return true
  }

  const onScan = async () => {
    try {
      Logger.info('DocumentScanner: Attempting to open the document scanner plugin')
      let scannedImages: string[] | undefined = []

      if (isIOS) {
        const { scannedImages: images } = await WeScan.scanDocument()
        scannedImages = images
      } else {
        const hasPermissions = await checkCameraPermissions()
        if (!hasPermissions) {
          return
        }

        const { scannedImages: images } = await DocumentScannerPlugin.scanDocument({
          responseType: ResponseType.Base64,
          maxNumDocuments: 1,
          croppedImageQuality: 40,
        })
        scannedImages = images
      }

      if (scannedImages?.length && scannedImages.length > 0) {
        const newScannedDocuments = scannedImages.map((scannedImageSrc) => {
          const newScannedDocument: IScannedDocument = {
            id: uuidv4(),
            imageSource: scannedImageSrc,
            name: '',
            category: null,
          }

          return newScannedDocument
        })

        Logger.info('DocumentScanner: User completed the plugin flow with a scanned document')
        if (!isActiveRef.current) return
        dispatch(setScannedDocuments(newScannedDocuments))
      } else {
        Logger.info('DocumentScanner: User canceled out of the plugin flow')
        onClose()
      }
    } catch (e) {
      Logger.info(`DocumentScanner: Error initializing the plugin: ${e}`)
      if (!isActiveRef.current) return
      if (!scannerError) setScannerError(t('documentScanner.ScannerError'))
    } finally {
      setIsLoading(false)
    }
  }

  const onSave = () => {
    const hasNoCategoryDoc = scannedDocuments.some((sd) => !sd.category)
    if (PR_REQ_CHART_ATTACH_CATEGORY === 1 && hasNoCategoryDoc) {
      const alertState = {
        header: 'Warning',
        message: `${t('attachments.CategoryRequiredMsg')}`,
      }
      dispatch(showAlert(alertState))
      return
    }
    dispatch(setDisableScanButton(true))
    dispatch(importDocuments())
    Logger.info('DocumentScanner: User saved a scanned document')
    history.push(TabConstants.ATTACHMENT_TAB)
  }

  const onDocumentEditName = (id: string, name: string) => {
    const editedDocumentIndex = scannedDocuments.findIndex((scannedDocument) => {
      return scannedDocument.id === id
    })
    let editedDocument = scannedDocuments[editedDocumentIndex]

    editedDocument = {
      ...editedDocument,
      name: name,
    }
    scannedDocuments[editedDocumentIndex] = editedDocument

    dispatch(setScannedDocuments(scannedDocuments))
  }

  const onDocumentEditCategory = (id: string, category: IOption | null) => {
    const editedDocumentIndex = scannedDocuments.findIndex((scannedDocument) => {
      return scannedDocument.id === id
    })
    let editedDocument = scannedDocuments[editedDocumentIndex]

    editedDocument = {
      ...editedDocument,
      category: category,
    }
    scannedDocuments[editedDocumentIndex] = editedDocument

    dispatch(setScannedDocuments(scannedDocuments))
  }

  const onDocumentDelete = (id: string) => {
    const newScannedDocuments = scannedDocuments.filter((scannedDocument) => {
      return scannedDocument.id !== id
    })

    dispatch(setScannedDocuments(newScannedDocuments))

    if (newScannedDocuments.length === 0) {
      setIsLoading(true)
      onScan()
    }
  }

  const renderHeader = () => {
    return (
      <IonHeader className='ion-no-padding'>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Cancel' color='primary-alt' onClick={onBackClick}>
              {t('common.buttons.Cancel')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' color='primary-alt' fill='clear' onClick={onSave}>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('documentScanner.ScannedDocument')}</IonTitle>
        </IonToolbar>
      </IonHeader>
    )
  }

  const renderLoader = () => {
    return (
      <div className='document-items-loader'>
        <IonSpinner />
      </div>
    )
  }

  const renderLoaderOrError = () => {
    if (!scannerError) {
      return <>{renderLoader()}</>
    }

    return (
      <IonList className='grid-ion-list document-items-list'>
        <div className='document-items-empty'>
          <IonText>{scannerError}</IonText>
        </div>
      </IonList>
    )
  }

  const renderDocumentsList = () => {
    return (
      <>
        <IonList className='grid-ion-list document-items-list'>
          {scannedDocuments.map((scannedDocument, i) => (
            <DocumentItem
              key={scannedDocument.id}
              documentId={scannedDocument.id}
              imageSource={scannedDocument.imageSource}
              onDocumentEditName={onDocumentEditName}
              onDocumentEditCategory={onDocumentEditCategory}
              onDocumentDelete={onDocumentDelete}
            />
          ))}
        </IonList>
      </>
    )
  }

  return (
    <IonPage id='DocumentScannerPage' data-testid='DocumentScanner'>
      {renderHeader()}
      <IonContent fullscreen color='light'>
        {isLoading && <>{renderLoader()}</>}
        {!isLoading && scannedDocuments.length === 0 && <>{renderLoaderOrError()}</>}
        {!isLoading && scannedDocuments.length > 0 && <>{renderDocumentsList()}</>}
      </IonContent>
    </IonPage>
  )
}

export default DocumentScanner
